import { useState } from "react";
import { Briefcase, UserCog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChannelBadge, Panel, StageBadge } from "@/components/crm/Badges";
import { ReassignDialog } from "@/components/crm/lead/ReassignDialog";
import { StartWhatsAppButton, WelcomeChips } from "@/components/crm/WelcomeWhatsApp";
import { brand } from "@/lib/brand";

const Row = ({ label, children, testid }) => (
  <div className="flex items-center justify-between gap-4 py-2.5">
    <dt className="text-xs font-medium text-slate-500">{label}</dt>
    <dd className="text-right text-sm font-medium" data-testid={testid}>{children}</dd>
  </div>
);

export const CommercialPanel = ({ lead, services, onUpdate, assignments, sellers = [], reload }) => {
  const { user } = useAuth();
  const [reassigning, setReassigning] = useState(false);
  const canReassign = user.role !== "vendedor";
  const last = assignments[0];
  return (
    <Panel title="Información comercial" icon={Briefcase} testid="lead-commercial-panel"
           action={canReassign && (
             <Button variant="outline" size="sm" onClick={() => setReassigning(true)} className="h-8 rounded-lg" data-testid="reassign-button">
               <UserCog className="mr-1 h-3.5 w-3.5" /> {lead.assigned_seller_id ? "Reasignar" : "Asignar"}
             </Button>
           )}>
      {canReassign && <ReassignDialog open={reassigning} onOpenChange={setReassigning} lead={lead} sellers={sellers} onDone={reload} />}
      <dl className="divide-y divide-slate-100">
        <Row label="Canal" testid="lead-channel"><ChannelBadge channel={lead.channel} /></Row>
        <Row label="Servicio">
          <Select value={lead.service_id || undefined} onValueChange={(service_id) => onUpdate({ service_id })}>
            <SelectTrigger className="h-8 w-44 rounded-lg text-xs" data-testid="lead-service-select"><SelectValue placeholder="Sin servicio" /></SelectTrigger>
            <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{!s.active && " (inactivo)"}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        <Row label="Vendedor" testid="lead-seller">{lead.assigned_seller_name || <span className="text-rose-600">Sin asignar</span>}</Row>
        <Row label="Estado" testid="lead-stage"><StageBadge stage={lead.stage} /></Row>
        <Row label="Fecha de ingreso" testid="lead-created-at">{fmtDateTime(lead.created_at)}</Row>
        <Row label="¿Cómo nos conoció?">{lead.referral_source || "—"}</Row>
        {!lead.seed && (lead.tag_status?.["formulario-web"] || lead.tag_status?.["iniciar-whatsapp"] || lead.phone) && (
          <Row label="Bienvenida WhatsApp" testid="lead-welcome-row">
            <span className="flex flex-col items-end gap-2">
              <WelcomeChips tagStatus={lead.tag_status} welcomeAt={lead.welcome_sent_at} testid="lead-welcome-status" />
              <StartWhatsAppButton leadId={lead.id} name={lead.full_name} phone={lead.phone} seed={lead.seed} onDone={reload} className="h-8 text-xs" />
            </span>
          </Row>
        )}
        <Row label="Mensaje del formulario" testid="lead-form-message"><span className="whitespace-pre-line">{lead.message || "—"}</span></Row>
        {last && <Row label="Asignación" testid="lead-assignment-method">{last.method} · {fmtDateTime(last.assigned_at)}</Row>}
        <Row label="Plataforma de mensajería" testid="lead-ghl-status">{lead.seed ? <span className="text-slate-400">Demo (no se sincroniza)</span> : lead.ghl_contact_id ? <span className="text-emerald-600">Sincronizado</span> : <span className="text-amber-600" title={brand(lead.ghl_sync_error || "")}>Pendiente de sincronización</span>}</Row>
      </dl>
    </Panel>
  );
};
